import { http } from "@/lib/api";

const q = (o = {}) => {
  const r = {};

  Object.entries(o).forEach(([k, v]) => {
    if (
      v !== undefined &&
      v !== null &&
      v !== "" &&
      v !== "All"
    ) {
      r[k] = v;
    }
  });

  return r;
};

const BILLING_BASE = "/api/billing";

const UNAUTHORISED_MESSAGE =
  "You are Not Authorised for this action. Contact Admin.";

const handleBillingError = (error) => {
  if (error?.response?.status === 403) {
    error.userMessage = UNAUTHORISED_MESSAGE;
  }

  return error;
};

export const billingApi = {
  // ==================================================
  // RATE MASTER
  // ==================================================

  listRates: async (opts = {}) =>
    (
      await http.get(`${BILLING_BASE}/rates`, {
        params: q(opts),
      })
    ).data,

  createRate: async (payload) =>
    (
      await http.post(
        `${BILLING_BASE}/rates`,
        payload
      )
    ).data,

  updateRate: async (id, payload) =>
    (
      await http.patch(
        `${BILLING_BASE}/rates/${encodeURIComponent(id)}`,
        payload
      )
    ).data,

  deleteRate: async (id) => {
    try {
      return (
        await http.delete(
          `${BILLING_BASE}/rates/${encodeURIComponent(id)}`
        )
      ).data;
    } catch (error) {
      throw handleBillingError(error);
    }
  },

  toggleRate: async (id) =>
    (
      await http.post(
        `${BILLING_BASE}/rates/${encodeURIComponent(id)}/toggle`
      )
    ).data,

  rateHistory: async (id) =>
    (
      await http.get(
        `${BILLING_BASE}/rates/${encodeURIComponent(id)}/history`
      )
    ).data,

  audit: async () =>
    (
      await http.get(
        `${BILLING_BASE}/rates/audit`
      )
    ).data,

  categories: async () =>
    (
      await http.get(
        `${BILLING_BASE}/rates/categories`
      )
    ).data,

  exportRatesUrl: () =>
    `${http.defaults.baseURL}${BILLING_BASE}/rates/export`,

  importRates: async (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return (
      await http.post(
        `${BILLING_BASE}/rates/import`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
    ).data;
  },

  // ==================================================
  // WCC
  // ==================================================

  parseWCC: async (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return (
      await http.post(
        `${BILLING_BASE}/wcc/parse`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
    ).data;
  },

  // ==================================================
  // INVOICES
  // ==================================================

  createInvoice: async (payload) =>
    (
      await http.post(
        `${BILLING_BASE}/invoices`,
        payload
      )
    ).data,

  listInvoices: async (opts = {}) =>
    (
      await http.get(
        `${BILLING_BASE}/invoices`,
        {
          params: q(opts),
        }
      )
    ).data,

  getInvoice: async (id) =>
    (
      await http.get(
        `${BILLING_BASE}/invoices/${encodeURIComponent(id)}`
      )
    ).data,

  deleteInvoice: async (id) => {
    try {
      return (
        await http.delete(
          `${BILLING_BASE}/invoices/${encodeURIComponent(id)}`
        )
      ).data;
    } catch (error) {
      throw handleBillingError(error);
    }
  },

  invoiceExcelUrl: (id) =>
    `${http.defaults.baseURL}${BILLING_BASE}/invoices/${encodeURIComponent(id)}/export`,

  invoicePdfUrl: (id) =>
    `${http.defaults.baseURL}${BILLING_BASE}/invoices/${encodeURIComponent(id)}/pdf`,

  updatePayment: async (id, payload) =>
    (
      await http.patch(
        `${BILLING_BASE}/invoices/${encodeURIComponent(id)}/payment`,
        payload
      )
    ).data,

  // ==================================================
  // DASHBOARD / COMPANY
  // ==================================================

  summary: async () =>
    (
      await http.get(
        `${BILLING_BASE}/dashboard/summary`
      )
    ).data,

  getCompany: async () =>
    (
      await http.get(
        `${BILLING_BASE}/company`
      )
    ).data,

  saveCompany: async (payload) =>
    (
      await http.post(
        `${BILLING_BASE}/company`,
        payload
      )
    ).data,

  // ==================================================
  // PAYMENTS
  // ==================================================

  addPayment: async (id, payload) =>
    (
      await http.post(
        `${BILLING_BASE}/invoices/${encodeURIComponent(id)}/payments`,
        payload
      )
    ).data,

  listPayments: async (id) =>
    (
      await http.get(
        `${BILLING_BASE}/invoices/${encodeURIComponent(id)}/payments`
      )
    ).data,

  deletePayment: async (paymentId) => {
    try {
      return (
        await http.delete(
          `${BILLING_BASE}/payments/${encodeURIComponent(paymentId)}`
        )
      ).data;
    } catch (error) {
      throw handleBillingError(error);
    }
  },

  // ==================================================
  // CUSTOMER STATEMENT
  // ==================================================

  statement: async (customer, start, end) =>
    (
      await http.get(
        `${BILLING_BASE}/statement`,
        {
          params: q({
            customer,
            start,
            end,
          }),
        }
      )
    ).data,

  // ==================================================
  // HISTORICAL EXCEL IMPORT
  // ==================================================

  historicalPreview: async (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return (
      await http.post(
        `${BILLING_BASE}/historical/preview`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        }
      )
    ).data;
  },

  historicalCommit: async (payload) =>
    (
      await http.post(
        `${BILLING_BASE}/historical/commit`,
        payload
      )
    ).data,

  historicalHistory: async () =>
    (
      await http.get(
        `${BILLING_BASE}/historical/history`
      )
    ).data,

  historicalTemplateUrl: () =>
    `${http.defaults.baseURL}${BILLING_BASE}/historical/template`,

  // ==================================================
  // INVOICE FILTER FACETS
  // ==================================================

  invoiceFacets: async () =>
    (
      await http.get(
        `${BILLING_BASE}/invoices-facets`
      )
    ).data,
};

export default billingApi;